import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { UserCircle2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { GymLoader } from "../../components/common/GymLoader";

const EMPTY = {
  name: "", email: "", whatsapp: "", dob: "", gender: "", blood_group: "", alternate_mobile: "", occupation: "",
  emergency_name: "", emergency_relationship: "", emergency_mobile: "",
};

const inputClass = "bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30";

// Self-editable personal/contact details — deliberately does NOT include phone (the OTP-login
// identifier, never member-editable) or anything staff-only (status, medical flags, KYC).
export default function Profile() {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);

  const profileQ = useQuery({ queryKey: ["member-profile"], queryFn: async () => (await api.get("/member/profile")).data });

  useEffect(() => {
    if (!profileQ.data) return;
    const p = profileQ.data;
    const ec = p.emergency_contact || {};
    setForm({
      name: p.name || "", email: p.email || "", whatsapp: p.whatsapp || "", dob: p.dob || "",
      gender: p.gender || "", blood_group: p.blood_group || "", alternate_mobile: p.alternate_mobile || "",
      occupation: p.occupation || "", emergency_name: ec.name || "", emergency_relationship: ec.relationship || "",
      emergency_mobile: ec.mobile || "",
    });
  }, [profileQ.data]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const saveMutation = useMutation({
    mutationFn: () => api.patch("/member/profile", {
      name: form.name || undefined, email: form.email || undefined, whatsapp: form.whatsapp || undefined,
      dob: form.dob || undefined, gender: form.gender || undefined, blood_group: form.blood_group || undefined,
      alternate_mobile: form.alternate_mobile || undefined, occupation: form.occupation || undefined,
      emergency_contact: form.emergency_name.trim()
        ? { name: form.emergency_name, relationship: form.emergency_relationship || undefined, mobile: form.emergency_mobile }
        : undefined,
    }),
    onSuccess: () => {
      toast.success("Profile saved.");
      queryClient.invalidateQueries({ queryKey: ["member-profile"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (profileQ.isLoading) return <GymLoader label="Loading profile…" testId="member-profile-loader" />;

  return (
    <div data-testid={MEMBER.profilePage} className="space-y-6">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
          <UserCircle2 size={22} color="#D6202B" strokeWidth={1.5} />
        </div>
        <div>
          <p className="font-heading text-xl font-bold">My profile</p>
          <p className="text-xs" style={{ color: "#A1A1AA" }}>{profileQ.data?.phone} · phone can't be changed here</p>
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="rounded-2xl p-5 space-y-3" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <p className="text-sm font-semibold">Personal details</p>
        <Field label="Full name"><Input data-testid={MEMBER.profileNameInput} value={form.name} onChange={set("name")} className={inputClass} /></Field>
        <Field label="Email"><Input type="email" data-testid={MEMBER.profileEmailInput} value={form.email} onChange={set("email")} className={inputClass} /></Field>
        <Field label="WhatsApp number"><Input data-testid={MEMBER.profileWhatsappInput} value={form.whatsapp} onChange={set("whatsapp")} className={inputClass} /></Field>
        <Field label="Alternate mobile"><Input data-testid={MEMBER.profileAlternateMobileInput} value={form.alternate_mobile} onChange={set("alternate_mobile")} className={inputClass} /></Field>
        <Field label="Date of birth"><Input type="date" data-testid={MEMBER.profileDobInput} value={form.dob} onChange={set("dob")} className={inputClass} /></Field>
        <Field label="Gender">
          <select data-testid={MEMBER.profileGenderSelect} value={form.gender} onChange={set("gender")} className={`w-full h-9 px-3 rounded-md text-sm ${inputClass} border`}>
            <option value="">Not set</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Blood group"><Input data-testid={MEMBER.profileBloodGroupInput} value={form.blood_group} onChange={set("blood_group")} placeholder="e.g. O+" className={inputClass} /></Field>
        <Field label="Occupation"><Input data-testid={MEMBER.profileOccupationInput} value={form.occupation} onChange={set("occupation")} className={inputClass} /></Field>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-2xl p-5 space-y-3" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <p className="text-sm font-semibold">Emergency contact</p>
        <Field label="Name"><Input data-testid={MEMBER.profileEmergencyNameInput} value={form.emergency_name} onChange={set("emergency_name")} className={inputClass} /></Field>
        <Field label="Relationship"><Input data-testid={MEMBER.profileEmergencyRelationshipInput} value={form.emergency_relationship} onChange={set("emergency_relationship")} placeholder="e.g. Spouse" className={inputClass} /></Field>
        <Field label="Mobile"><Input data-testid={MEMBER.profileEmergencyMobileInput} value={form.emergency_mobile} onChange={set("emergency_mobile")} className={inputClass} /></Field>
      </motion.div>

      <Button data-testid={MEMBER.profileSaveButton} disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()} className="w-full text-white" style={{ background: "#D6202B" }}>
        {saveMutation.isPending ? "Saving…" : "Save profile"}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium" style={{ color: "#A1A1AA" }}>{label}</label>
      {children}
    </div>
  );
}
